import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ROUTES } from '@/config/routes';
import { HottrMark } from '@/components/brand/HottrMark';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { Icon } from '@/components/ui/Icon';
import { EditorialImage } from '@/components/editorial/EditorialImage';
import { useProfiles } from '@/modules/profiles/useProfiles';
import { pickFeaturedProfile } from '@/modules/profiles/catalogue';
import { useMemo } from 'react';
import { cn } from '@/lib/cn';

interface AuthShellProps {
  eyebrow: string;
  title: ReactNode;
  /** Short editorial line below the title. */
  intro: ReactNode;
  children: ReactNode;
  /** Footer prompt — "Already inside?" / "Need an account?" link rows. */
  footer: ReactNode;
}

const EASE = [0.19, 1, 0.22, 1] as const;

/**
 * Shared frame for Login + Signup. Splits the viewport into a cinematic
 * left panel (B&W editorial photo) and a tight right panel for the form.
 *
 * On mobile: form stacks under the photo, photo becomes a slim top band
 * so the form doesn't get pushed off-screen on the smallest viewports.
 */
export const AuthShell = ({ eyebrow, title, intro, children, footer }: AuthShellProps) => {
  const { profiles } = useProfiles();
  const featured = useMemo(() => pickFeaturedProfile(profiles), [profiles]);

  return (
    <div className="grid min-h-dvh grid-cols-1 bg-ink text-text-primary lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/* LEFT — cinematic editorial */}
      <aside className="relative overflow-hidden border-b border-white/[0.06] lg:border-b-0 lg:border-r">
        {/* Min-height keeps the editorial readable on landscape phones
            where 36vh would otherwise collapse to ~120px. */}
        <div className="h-[36vh] min-h-[240px] lg:h-full lg:min-h-0">
          <EditorialImage
            scene={featured?.scene ?? 'club_room'}
            seed={featured?.sceneSeed ?? 1}
            photoUrl={featured?.photos?.[0]}
            aspect="aspect-auto"
            mono
            className="h-full"
          />
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(5,6,8,0.40) 0%, rgba(5,6,8,0.05) 40%, rgba(5,6,8,0.60) 100%)',
          }}
        />
        <header
          className="absolute inset-x-0 top-0 flex items-center justify-between gap-3 px-6 pt-6 md:px-10 md:pt-8"
          style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
        >
          <Link to={ROUTES.landing} aria-label="Back to HOTTR.">
            <HottrMark size="text-xl md:text-2xl" surface=".app" />
          </Link>
          <Link
            to={ROUTES.landing}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-1.5 border border-white/[0.10] bg-ink/85 px-3.5 py-2',
              'font-display text-[10px] font-bold uppercase tracking-widest text-text-secondary backdrop-blur',
              'transition-[color,border-color,background] duration-fast ease-signal',
              'hover:border-pink/40 hover:text-pink',
            )}
          >
            <Icon name="arrow_back" size={12} />
            Back
          </Link>
        </header>
        <p
          aria-hidden
          className="absolute bottom-5 left-6 hidden font-display text-[9px] font-bold uppercase tracking-[0.4em] text-text-muted md:left-10 lg:block"
        >
          NIGHT FILE · MMXXVI · MELBOURNE
        </p>
      </aside>

      {/* RIGHT — form */}
      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className={cn(
          'flex w-full flex-col justify-center',
          'px-6 py-12 md:px-12 md:py-16 lg:px-16',
        )}
        style={{ paddingBottom: 'max(3rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto w-full max-w-md">
          <SectionMarker className="mb-5">{eyebrow}</SectionMarker>
          <h1
            className="font-display font-black uppercase tracking-display"
            style={{
              fontSize: 'clamp(36px, 5vw, 64px)',
              lineHeight: 0.92,
              letterSpacing: '-0.045em',
            }}
          >
            {title}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-text-secondary md:text-base">
            {intro}
          </p>

          <div className="mt-9">{children}</div>

          <div className="mt-10 border-t border-white/[0.07] pt-5 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            {footer}
          </div>
        </div>
      </motion.main>
    </div>
  );
};
